// A vs B: two periods or two segments (creative, campaign, country, version, experiment variant...) side by side.
// Segment A = global filters; segment B = same environment, its own filters (URL params prefixed with b_).

import { type CompareResult, type FilterParams, pickFilters, previousPeriod, type Proportion, type RetentionResult, todayIn } from '@growth/client';
import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { LineChart } from '../components/charts.tsx';
import { DimensionFields, EnvironmentSwitch, type FilterLike, RangeSelect, useDimensions } from '../components/FilterBar.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { ChartCard, DataTable, ErrorBox } from '../components/ui.tsx';
import { describeFilters, useGlobalFilters } from '../filters.ts';
import { fmtCompact, fmtDeltaPct, fmtDuration, fmtInt, fmtNum, fmtPct, fmtPp } from '../format.ts';
import { ciText, funnelStages, MIN_SAMPLE, retentionSeries } from '../metrics.ts';
import { useApi } from '../session.tsx';
import { useGameTimezone } from './Overview.tsx';

const B = 'b_';

function useSegmentB(a: FilterParams, today: string | null): { params: FilterParams | null; filters: FilterLike; custom: boolean; reset: () => void } {
  const [sp, setSp] = useSearchParams();
  const raw = Object.fromEntries([...sp.entries()].filter(([k]) => k.startsWith(B)).map(([k, v]) => [k.slice(B.length), v]));
  const custom = Object.keys(raw).length > 0;
  const params = useMemo(() => {
    if (!today) return null;
    if (!custom) return previousPeriod(a, today);
    const p = pickFilters(raw);
    if (!p.last && !p.from && !p.to) p.last = a.last ?? '30d';
    return { ...p, environment: a.environment };
  }, [sp.toString(), today, a]);
  const set = (patch: Record<string, string | undefined | null>) =>
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        // First edit of B materializes its defaults so the other fields keep their values.
        if (![...next.keys()].some((k) => k.startsWith(B)) && params) {
          for (const [k, v] of Object.entries(params)) if (k !== 'environment' && v !== undefined && v !== '') next.set(B + k, String(v));
        }
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === null || v === '') next.delete(B + k);
          else next.set(B + k, v);
        }
        if (![...next.keys()].some((k) => k.startsWith(B))) next.set(`${B}last`, a.last ?? '30d');
        return next;
      },
      { replace: true },
    );
  const reset = () =>
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const k of [...next.keys()]) if (k.startsWith(B)) next.delete(k);
        return next;
      },
      { replace: true },
    );
  return { params, filters: { params: params ?? {}, set }, custom, reset };
}

/** Do the two 95% intervals overlap? (conservative "is this difference clear?" check) */
function verdict(a: Proportion | undefined, b: Proportion | undefined): string {
  if (!a || !b || a.rate === null || b.rate === null) return 'sin datos';
  if (a.n < MIN_SAMPLE || b.n < MIN_SAMPLE) return 'muestra insuficiente';
  if (a.ci_high! < b.ci_low! || b.ci_high! < a.ci_low!) return b.rate > a.rate ? 'B mejor (IC no se solapan)' : 'A mejor (IC no se solapan)';
  return 'no concluyente';
}

export function ComparePage() {
  const { gameId = '' } = useParams();
  const filters = useGlobalFilters();
  const tz = useGameTimezone(gameId);
  const today = tz ? todayIn(tz) : null;
  const b = useSegmentB(filters.params, today);
  const dims = useDimensions(gameId, filters.environment);
  const bKey = JSON.stringify(b.params);

  const cmp = useApi<CompareResult>(b.params ? `cmp:${gameId}:${filters.key}:${bKey}` : null, (api, s) => api.compare(gameId, filters.params, b.params!, s));
  const retA = useApi<RetentionResult>(`ret:${gameId}:${filters.key}:none`, (api, s) => api.retention(gameId, { ...filters.params, group_by: 'none' }, s));
  const retB = useApi<RetentionResult>(b.params ? `ret:${gameId}:b:${bKey}` : null, (api, s) => api.retention(gameId, { ...b.params!, group_by: 'none' }, s));
  const c = cmp.data;
  const stale = cmp.loading && !!c;
  const gA = retA.data?.groups[0];
  const gB = retB.data?.groups[0];

  const metric = (label: string, key: string, fmt: (v: number | null) => string) => {
    const d = c?.deltas[key];
    return [label, fmt(d?.a ?? null), fmt(d?.b ?? null), d?.delta_pct === null || d?.delta_pct === undefined ? '—' : fmtDeltaPct(d.delta_pct), ''];
  };
  const rate = (label: string, pa: Proportion | undefined, pb: Proportion | undefined) => [
    label,
    `${fmtPct(pa?.rate)} (${ciText(pa)})`,
    `${fmtPct(pb?.rate)} (${ciText(pb)})`,
    pa?.rate !== null && pb?.rate !== null && pa && pb ? fmtPp((pb.rate ?? 0) - (pa.rate ?? 0)) : '—',
    verdict(pa, pb),
  ];
  const day = (o: CompareResult['a'] | undefined, d: number) => o?.retention?.days.find((x) => x.day === d)?.classic;

  const seriesA = c?.a.engagement.series ?? [];
  const seriesB = c?.b.engagement.series ?? [];
  const n = Math.max(seriesA.length, seriesB.length);
  const stagesA = c ? funnelStages(c.a.activation) : [];
  const stagesB = c ? funnelStages(c.b.activation) : [];

  return (
    <>
      <PageHeader title="Comparar A/B" sub="Dos periodos o dos segmentos con el mismo cálculo. Las diferencias de tasas solo se marcan como claras si sus IC 95 % no se solapan." />
      <div className="filterbar" aria-label="Segmento A">
        <strong style={{ alignSelf: 'center' }}>A</strong>
        <EnvironmentSwitch filters={filters} />
        <DimensionFields filters={filters} dims={dims.data} />
      </div>
      <div className="filterbar" aria-label="Segmento B">
        <strong style={{ alignSelf: 'center' }}>B</strong>
        <DimensionFields filters={b.filters} dims={dims.data} range={false} />
        <RangeSelect filters={b.filters} />
        <span className="spacer" />
        <button type="button" className="btn small" disabled={!b.custom} onClick={b.reset}>
          B = periodo anterior
        </button>
      </div>
      <p className="muted small" style={{ marginTop: -6 }}>
        A: {c ? `${c.a.filters.from} → ${c.a.filters.to}` : '…'} {describeFilters(filters.params) && `· ${describeFilters(filters.params)}`} — B: {c ? `${c.b.filters.from} → ${c.b.filters.to}` : '…'}{' '}
        {b.params && describeFilters(b.params) && `· ${describeFilters(b.params)}`}
      </p>
      {cmp.error && <ErrorBox error={cmp.error} onRetry={cmp.reload} />}

      {c && (
        <>
          <div className={`card loading-frame${stale ? ' stale' : ''}`} style={{ marginBottom: 16 }}>
            <DataTable
              data={{
                columns: [{ label: 'Métrica' }, { label: 'A', numeric: true }, { label: 'B', numeric: true }, { label: 'B vs A', numeric: true }, { label: 'Lectura' }],
                rows: [
                  metric('DAU medio', 'dau_avg', fmtCompact),
                  metric('Instalaciones', 'new_users', fmtInt),
                  metric('Sesiones por DAU', 'sessions_per_dau', (v) => fmtNum(v, 2)),
                  metric('Sesión media', 'avg_session_s', fmtDuration),
                  metric('Sesión mediana', 'median_session_s', fmtDuration),
                  metric('Tiempo de juego por DAU', 'playtime_per_dau_s', fmtDuration),
                  metric('Runs por jugador', 'runs_per_player', (v) => fmtNum(v, 2)),
                  metric('Duración media de run', 'avg_run_s', fmtDuration),
                  rate('Retención D1', day(c.a, 1), day(c.b, 1)),
                  rate('Retención D7', day(c.a, 7), day(c.b, 7)),
                  rate('Retención D30', day(c.a, 30), day(c.b, 30)),
                  rate('Tutorial completado', c.a.activation.tutorial_completion_rate, c.b.activation.tutorial_completion_rate),
                  rate('Segunda run', c.a.activation.second_run_rate, c.b.activation.second_run_rate),
                  rate('Ven un rewarded', c.a.activation.rewarded_ad_user_rate, c.b.activation.rewarded_ad_user_rate),
                ],
              }}
            />
          </div>

          <div className="grid cols-2">
            <ChartCard
              title="Retención A vs B"
              subtitle="Clásica · bandas = IC 95 %"
              stale={stale}
              table={{
                columns: [{ label: 'Día' }, { label: 'A', numeric: true }, { label: 'B', numeric: true }],
                rows: (gA ?? gB)?.days.map((d) => {
                  const a = gA?.days.find((x) => x.day === d.day);
                  const b = gB?.days.find((x) => x.day === d.day);
                  return [`D${d.day}`, a && a.mature ? `${fmtPct(a.classic.rate)} (n=${a.mature})` : '—', b && b.mature ? `${fmtPct(b.classic.rate)} (n=${b.mature})` : '—'];
                }) ?? [],
              }}
            >
              {gA || gB ? (
                <LineChart
                  ariaLabel="Curvas de retención A y B"
                  x={(gA ?? gB)!.days.map((d) => d.day)}
                  linearX
                  formatX={(v) => `D${v}`}
                  formatY={(v) => fmtPct(v, 0)}
                  formatValue={(v) => fmtPct(v)}
                  yCap={1}
                  series={[...(gA ? [retentionSeries(gA, 'classic', 1, 'A')] : []), ...(gB ? [retentionSeries(gB, 'classic', 2, 'B')] : [])]}
                />
              ) : (
                <div className="empty">Sin cohortes.</div>
              )}
            </ChartCard>
            <ChartCard
              title="DAU por día del periodo"
              subtitle="Alineado por día relativo (día 1 = primer día de cada periodo)"
              stale={stale}
              table={{
                columns: [{ label: 'Día' }, { label: 'Fecha A' }, { label: 'DAU A', numeric: true }, { label: 'Fecha B' }, { label: 'DAU B', numeric: true }],
                rows: Array.from({ length: n }, (_, i) => [i + 1, seriesA[i]?.date ?? '—', fmtInt(seriesA[i]?.dau), seriesB[i]?.date ?? '—', fmtInt(seriesB[i]?.dau)]),
              }}
            >
              <LineChart
                ariaLabel="DAU de A y B por día relativo"
                x={Array.from({ length: n }, (_, i) => i + 1)}
                formatX={(v) => `día ${v}`}
                formatY={fmtCompact}
                formatValue={fmtInt}
                series={[
                  { key: 'a', label: 'A', slot: 1, values: Array.from({ length: n }, (_, i) => seriesA[i]?.dau ?? null) },
                  { key: 'b', label: 'B', slot: 2, values: Array.from({ length: n }, (_, i) => seriesB[i]?.dau ?? null) },
                ]}
              />
            </ChartCard>
          </div>

          <ChartCard title="Activación A vs B" subtitle="% de las instalaciones de cada cohorte" stale={stale}>
            <DataTable
              data={{
                columns: [{ label: 'Etapa' }, { label: 'A', numeric: true }, { label: 'B', numeric: true }, { label: 'B vs A', numeric: true }, { label: 'Lectura' }],
                rows: stagesA.map((s, i) => {
                  const sb = stagesB[i];
                  if (!s.rate || !sb?.rate) return [s.label, fmtInt(s.count), fmtInt(sb?.count), '—', ''];
                  return [s.label, fmtPct(s.rate.rate), fmtPct(sb.rate.rate), fmtPp((sb.rate.rate ?? 0) - (s.rate.rate ?? 0)), verdict(s.rate, sb.rate)];
                }),
              }}
            />
          </ChartCard>
        </>
      )}
    </>
  );
}
